/**
 * Centralized Analysis Type Configuration
 *
 * This module provides a single source of truth for:
 * - Analysis type configurations (icon, color, label, URL part)
 *
 * Status icon configuration has moved to @/lib/constants/status.
 */

import {
  SievingIcon,
  VisionSystemIcon,
  WashLossIcon,
} from "@/components/ui/icons/analysis-icons";
import { BoxSelect } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

// Re-export status types and constants from their new home for backward compat
export {
  DEFAULT_STATUS_ICON,
  STATUS_ICONS,
  type HeroIcon,
  type StatusIconConfigItem,
  type StatusIconKey,
} from "@/lib/constants/status";

// =============================================================================
// TYPES
// =============================================================================

export type AnalysisTypeKey = "image_analysis" | "gradation" | "wash_loss";

export type AnalysisColor = "purple" | "sky" | "amber" | "gray";

export interface AnalysisTypeConfigItem {
  key: AnalysisTypeKey | "unknown";
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  color: AnalysisColor;
  urlPart: string;
  keywords: readonly string[];
}

// =============================================================================
// ANALYSIS TYPE CONFIGURATION
// =============================================================================

/**
 * Configuration for each analysis type.
 * Used to determine icon, color, and routing based on analysis type name keywords.
 * Display names come from the backend — not from this config.
 */
export const ANALYSIS_TYPE_CONFIG: Record<
  AnalysisTypeKey,
  AnalysisTypeConfigItem
> = {
  image_analysis: {
    key: "image_analysis",
    icon: VisionSystemIcon,
    color: "purple",
    urlPart: "image",
    keywords: ["image", "imaging", "static", "static imaging"],
  },
  gradation: {
    key: "gradation",
    icon: SievingIcon,
    color: "sky",
    urlPart: "gradation",
    keywords: ["sieve", "gradation", "astm c136", "sieve analysis"],
  },
  wash_loss: {
    key: "wash_loss",
    icon: WashLossIcon,
    color: "amber",
    urlPart: "washloss",
    keywords: ["wash", "loss", "wash_loss", "astm c117", "sieve wash"],
  },
} as const;

/**
 * Default/fallback configuration for unknown analysis types.
 */
export const DEFAULT_ANALYSIS_TYPE_CONFIG: AnalysisTypeConfigItem = {
  key: "unknown",
  icon: BoxSelect,
  color: "gray",
  urlPart: "",
  keywords: [],
};

// =============================================================================
// ANALYSIS STATUS VALUES (from backend OpenAPI enum)
// =============================================================================

/**
 * All valid analysis status values as documented by the backend OpenAPI spec
 * for GET /api/v1/analyses/assigned-to-me.
 *
 * Kept as a readonly tuple so it can be used both as a runtime array (for
 * UI filter dropdowns) and as the source for the AnalysisStatus TS type.
 *
 * TODO(backend): Replace this hardcoded constant with a call to
 * `GET /api/v1/analyses/status-values` (clone of /analysis/priorities)
 * once the backend ships that endpoint.
 */
export const ANALYSIS_STATUSES = [
  "pending",
  "queued",
  "processing",
  "sign_off_requested",
  "completed",
  "cancelled",
  "failed",
  "archived",
  "processed",
] as const;

/**
 * Statuses where POST /analyses/{id}/archive is not allowed (OpenAPI v2.3.96).
 * Completed/failed/processed/sign_off_requested are archivable; signed-off
 * (completed) analyses are auto-voided then archived on the backend.
 *
 * Image queued/processing must use static-imaging cancel instead — that
 * case is handled separately via Cancel in the layout, not this set.
 */
export const ARCHIVE_BLOCKED_STATUSES = new Set([
  "pending",
  "cancelled",
  "archived",
]);

/**
 * Whether the Archive overflow action should be shown for a given status.
 * Does not require is_editable — completed (locked) analyses are archivable.
 */
export function canShowArchiveAction(status: string): boolean {
  return !ARCHIVE_BLOCKED_STATUSES.has(status.toLowerCase());
}

// =============================================================================
// ANALYSIS PRIORITY VALUES (from backend OpenAPI AnalysisPriority enum)
// =============================================================================

/**
 * Fallback / placeholder analysis priority levels.
 *
 * Membership is owned by GET /api/v1/analysis/priorities (via
 * `useAnalysisPriorities`). Keep this tuple as the typed default when the
 * request has not resolved or the API returns an empty list.
 */
export const ANALYSIS_PRIORITIES = ["low", "normal", "high", "urgent"] as const;

export type AnalysisPriority = (typeof ANALYSIS_PRIORITIES)[number];

/** OpenAPI AnalysisBulkUpdate / workflow / delete: max analysis UUIDs per request. */
export const MAX_BULK_ANALYSIS_IDS = 100;

// =============================================================================
// FORM DEFAULTS & CONSTANTS
// =============================================================================

/**
 * Default mass unit for analysis forms.
 * Used as initial value when no unit is specified.
 */
export const DEFAULT_MASS_UNIT = "g" as const;

/**
 * Human-readable labels for mass unit codes.
 */
export const MASS_UNIT_LABELS: Record<string, string> = {
  g: "g",
  kg: "kg",
  lb: "lb",
};

/**
 * Build combobox-compatible option objects from a list of string values.
 *
 * @param items - Raw values from the API
 * @param prefix - Prefix for generating stable option IDs
 * @param labels - Optional lookup for human-readable labels
 */
export function buildSelectOptions(
  items: string[] | undefined,
  prefix: string,
  labels?: Record<string, string>,
): Array<{ value: string; label: string; id: string }> {
  if (!items) return [];
  return items.map((item, index) => ({
    value: item,
    label: labels?.[item] ?? item,
    id: `${prefix}-${index}`,
  }));
}

// =============================================================================
// ANALYSIS TYPE COLOR CLASSES
// =============================================================================

/**
 * Tailwind classes for analysis type pill backgrounds and text.
 * Uses more saturated colors for better visibility and contrast.
 */
export const ANALYSIS_TYPE_COLORS: Record<
  AnalysisColor,
  { bg: string; text: string; border: string; iconBg: string }
> = {
  purple: {
    bg: "bg-purple-50",
    text: "text-purple-800",
    border: "border-purple-300",
    iconBg: "bg-purple-200/60",
  },
  sky: {
    bg: "bg-sky-50",
    text: "text-sky-800",
    border: "border-sky-300",
    iconBg: "bg-sky-200/60",
  },
  amber: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-300",
    iconBg: "bg-amber-200/60",
  },
  gray: {
    bg: "bg-gray-50",
    text: "text-gray-700",
    border: "border-gray-300",
    iconBg: "bg-gray-200/60",
  },
};
